import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, X, Loader2 } from 'lucide-react';
import JourneyStrip from './JourneyStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const journeySteps = [
  { id: 'discover', label: 'Discover' },
  { id: 'reserve', label: 'Reserve' },
  { id: 'confirmed', label: 'Confirmed' },
];

const Shell = ({ children, currentStep = 'confirmed', tone = 'success' }) => (
  <div className="min-h-screen bg-onyx flex flex-col items-center justify-center p-6 grain relative overflow-hidden">
    <div
      aria-hidden="true"
      className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-[0.15]"
      style={{ background: 'radial-gradient(circle, #D1679A 0%, transparent 60%)' }}
    />
    <div className="relative w-full max-w-xl">
      <div className="mb-8">
        <JourneyStrip steps={journeySteps} currentStep={currentStep} tone={tone} />
      </div>
      <div className="bg-carbon border border-pewter/70 p-10 md:p-14">
        {children}
      </div>
    </div>
  </div>
);

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState('');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setError('No payment session found');
      setPaymentStatus('error');
      return;
    }
    const check = async (attempts = 0) => {
      if (attempts >= 5) {
        setError('Payment status check timed out. Contact support if the payment completed.');
        setPaymentStatus('timeout');
        return;
      }
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === 'paid') {
          setPaymentDetails(data);
          setPaymentStatus('success');
          return;
        }
        if (data.status === 'expired') {
          setError('Payment session expired. Please try booking again.');
          setPaymentStatus('expired');
          return;
        }
        setTimeout(() => check(attempts + 1), 2000);
      } catch (e) {
        setError('Error checking payment status.');
        setPaymentStatus('error');
      }
    };
    check();
  }, [sessionId]);

  if (paymentStatus === 'checking') {
    return (
      <Shell currentStep="reserve" tone="forward">
        <div className="flex flex-col items-center text-center">
          <Loader2 className="animate-spin text-blush mb-6" size={28} />
          <p className="eyebrow mb-3">Processing</p>
          <h2 className="font-serif text-ivory text-2xl">Confirming your reservation…</h2>
        </div>
      </Shell>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <Shell currentStep="confirmed" tone="success">
        <div className="text-center">
          <div className="w-14 h-14 border border-blush flex items-center justify-center mx-auto mb-6">
            <Check className="text-blush" size={18} />
          </div>
          <p className="eyebrow mb-3">Confirmed</p>
          <h1 className="font-serif text-ivory text-3xl md:text-4xl mb-4">
            Your Privé ritual is <span className="brand-italic chrome-rose">reserved.</span>
          </h1>
          <p className="text-silverx text-sm leading-relaxed mb-8">
            Thank you for booking with Snatched Beauties Privé. A confirmation has been sent to
            your email.
          </p>

          {paymentDetails && (
            <div className="text-left border border-pewter/60 p-6 mb-8 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-silverx">Amount</span>
                <span className="text-ivory">
                  {(() => {
                    const dollars = (paymentDetails.amount_total || 0) / 100;
                    const formatted = Number.isInteger(dollars)
                      ? `$${dollars}`
                      : `$${dollars.toFixed(2)}`;
                    return `${formatted} ${paymentDetails.currency?.toUpperCase() || ''}`;
                  })()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-silverx">Service</span>
                <span className="text-ivory">
                  {paymentDetails.metadata?.description || 'Privé Treatment'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-silverx">Status</span>
                <span className="chrome-rose brand-italic capitalize">
                  {paymentDetails.payment_status}
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/')}
              className="btn-pill flex-1 inline-flex justify-center items-center gap-2 px-6 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush"
            >
              Return Home
            </button>
            <button
              onClick={() => navigate('/#booking')}
              className="btn-pill flex-1 inline-flex justify-center items-center gap-2 px-6 py-4 rounded-full border border-pewter text-ivory text-[11px] tracking-[0.26em] uppercase hover:border-blush hover:text-blush"
            >
              Book Another
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell currentStep="reserve" tone="paused">
      <div className="text-center">
        <div className="w-14 h-14 border border-pewter flex items-center justify-center mx-auto mb-6">
          <X className="text-silverx" size={18} />
        </div>
        <p className="eyebrow mb-3">
          {paymentStatus === 'timeout' ? 'Timeout' : 'Payment Issue'}
        </p>
        <h1 className="font-serif text-ivory text-2xl md:text-3xl mb-3">Something needs attention.</h1>
        <p className="text-silverx text-sm leading-relaxed mb-8">{error}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => window.location.reload()}
            className="btn-pill flex-1 inline-flex justify-center items-center gap-2 px-6 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/')}
            className="btn-pill flex-1 inline-flex justify-center items-center gap-2 px-6 py-4 rounded-full border border-pewter text-ivory text-[11px] tracking-[0.26em] uppercase hover:border-blush hover:text-blush"
          >
            Return Home
          </button>
        </div>
        <p className="text-silverx text-[11px] tracking-[0.2em] uppercase mt-8">
          Need help?{' '}
          <a href="mailto:support@snatchedbeauties.com" className="text-ivory hover:text-blush">
            support@snatchedbeauties.com
          </a>
        </p>
      </div>
    </Shell>
  );
};

export default PaymentSuccess;
